import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function RateLimitManagement() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchIP, setSearchIP] = useState('');

  const fetchRateLimits = useCallback(async () => {
    try {
      const response = await adminAPI.getRateLimitStatus();
      setData(response.data.data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch rate limits:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRateLimits();

    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchRateLimits, refreshInterval * 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchRateLimits, autoRefresh, refreshInterval]);

  const handleUnlock = async (key) => {
    setActionLoading(key);
    try {
      await adminAPI.unlockRateLimitKey(key);
      await fetchRateLimits();
    } catch (err) {
      alert(`Failed to unlock: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnlockAllForIP = async (ip) => {
    setActionLoading(`ip-${ip}`);
    try {
      await adminAPI.unlockRateLimitIP(ip);
      await fetchRateLimits();
    } catch (err) {
      alert(`Failed to unlock IP: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear ALL rate limits? This will unlock all locked IPs.')) {
      return;
    }
    setActionLoading('clear-all');
    try {
      await adminAPI.clearAllRateLimits();
      await fetchRateLimits();
    } catch (err) {
      alert(`Failed to clear: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const formatTimeRemaining = (seconds) => {
    if (seconds <= 0) return 'Expired';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const getStatusColor = (isLocked) => {
    return isLocked ? '#ef4444' : '#10b981';
  };

  const filteredRecords = data?.records?.filter(record => {
    if (!searchIP) return true;
    return record.ip.includes(searchIP) || record.path.includes(searchIP);
  }) || [];

  if (loading) {
    return (
      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', minHeight: '100vh' }}>
        <Navbar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <div style={{ color: 'white', fontSize: '24px' }}>Loading rate limit data...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }}>
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            color: 'white',
            marginBottom: 'var(--space-sm)'
          }}>
            Rate Limit Management
          </h1>
          <p style={{ color: '#94a3b8', fontSize: 'var(--font-size-lg)' }}>
            Monitor and manage brute force protection & rate limiting
          </p>
          <div style={{ marginTop: 'var(--space-md)', display: 'flex', justifyContent: 'center', gap: 'var(--space-md)', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={fetchRateLimits}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--primary)',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Refresh Now
            </button>
            <button
              onClick={handleClearAll}
              disabled={actionLoading === 'clear-all' || !data?.records?.length}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#ef4444',
                color: 'white',
                cursor: data?.records?.length ? 'pointer' : 'not-allowed',
                fontWeight: '600',
                opacity: data?.records?.length ? 1 : 0.5
              }}
            >
              {actionLoading === 'clear-all' ? 'Clearing...' : 'Clear All Limits'}
            </button>
            <label style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.1)',
                color: 'white'
              }}
            >
              <option value={5}>5s</option>
              <option value={10}>10s</option>
              <option value={30}>30s</option>
              <option value={60}>60s</option>
            </select>
            {lastRefresh && (
              <span style={{ color: '#64748b', fontSize: '14px' }}>
                Last updated: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #ef4444',
            borderRadius: '12px',
            padding: 'var(--space-md)',
            marginBottom: 'var(--space-xl)',
            color: '#fca5a5'
          }}>
            Error: {error}
          </div>
        )}

        {/* Settings & Summary Cards */}
        {data?.settings && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-lg)',
            marginBottom: 'var(--space-xl)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '16px',
              padding: 'var(--space-lg)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#60a5fa' }}>
                {data.settings.maxAttempts}
              </div>
              <div style={{ color: '#94a3b8', marginTop: '8px' }}>Max Attempts</div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.2) 0%, rgba(251, 146, 60, 0.1) 100%)',
              border: '1px solid rgba(251, 146, 60, 0.3)',
              borderRadius: '16px',
              padding: 'var(--space-lg)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#fb923c' }}>
                {data.settings.windowMinutes}m
              </div>
              <div style={{ color: '#94a3b8', marginTop: '8px' }}>Attempt Window</div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '16px',
              padding: 'var(--space-lg)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#ef4444' }}>
                {data.settings.lockoutMinutes}m
              </div>
              <div style={{ color: '#94a3b8', marginTop: '8px' }}>Lockout Duration</div>
            </div>

            <div style={{
              background: `linear-gradient(135deg, ${data.lockedCount > 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)'} 0%, ${data.lockedCount > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)'} 100%)`,
              border: `1px solid ${data.lockedCount > 0 ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
              borderRadius: '16px',
              padding: 'var(--space-lg)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: data.lockedCount > 0 ? '#ef4444' : '#10b981' }}>
                {data.lockedCount}/{data.totalRecords}
              </div>
              <div style={{ color: '#94a3b8', marginTop: '8px' }}>Locked/Total</div>
            </div>
          </div>
        )}

        {/* Search */}
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <input
            type="text"
            placeholder="Search by IP or path..."
            value={searchIP}
            onChange={(e) => setSearchIP(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.1)',
              color: 'white',
              fontSize: '16px'
            }}
          />
        </div>

        {/* Records Table */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '16px',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: 'var(--space-lg)',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}>
            <h2 style={{ color: 'white', margin: 0 }}>Rate Limit Records</h2>
          </div>

          {filteredRecords.length === 0 ? (
            <div style={{
              padding: 'var(--space-2xl)',
              textAlign: 'center',
              color: '#94a3b8'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                {searchIP ? '0' : data?.totalRecords === 0 ? '0' : '0'}
              </div>
              <div>
                {searchIP
                  ? 'No records matching your search'
                  : 'No rate limit records found. System is clean!'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#94a3b8', fontWeight: '600' }}>IP Address</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#94a3b8', fontWeight: '600' }}>Path</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>Status</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>Attempts</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>Time Remaining</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: '#94a3b8', fontWeight: '600' }}>Last Attempt</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr
                      key={record.key}
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        background: record.isLocked ? 'rgba(239, 68, 68, 0.05)' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '12px 16px', color: 'white' }}>
                        <code style={{
                          background: 'rgba(255,255,255,0.1)',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}>
                          {record.ip}
                        </code>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '14px' }}>
                        {record.path}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          background: getStatusColor(record.isLocked),
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          {record.isLocked ? 'LOCKED' : 'Active'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: 'white', fontWeight: '600' }}>
                        {record.attemptCount} / {data.settings.maxAttempts}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', color: record.isLocked ? '#fca5a5' : '#94a3b8' }}>
                        {record.isLocked ? formatTimeRemaining(record.remainingLockSeconds) : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '13px' }}>
                        {record.lastAttempt ? new Date(record.lastAttempt).toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleUnlock(record.key)}
                            disabled={actionLoading === record.key}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              background: '#3b82f6',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                          >
                            {actionLoading === record.key ? '...' : 'Unlock'}
                          </button>
                          <button
                            onClick={() => handleUnlockAllForIP(record.ip)}
                            disabled={actionLoading === `ip-${record.ip}`}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              background: '#8b5cf6',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                            title="Unlock all paths for this IP"
                          >
                            {actionLoading === `ip-${record.ip}` ? '...' : 'Unlock IP'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '12px',
          padding: 'var(--space-lg)',
          marginTop: 'var(--space-xl)',
          color: '#93c5fd'
        }}>
          <strong>Understanding Rate Limits:</strong>
          <ul style={{ margin: '8px 0 0 20px', lineHeight: '1.8' }}>
            <li><strong>Max Attempts:</strong> Number of failed login attempts allowed before lockout ({data?.settings?.maxAttempts || 5} attempts).</li>
            <li><strong>Attempt Window:</strong> Time window during which attempts are counted ({data?.settings?.windowMinutes || 15} minutes).</li>
            <li><strong>Lockout Duration:</strong> How long an IP remains locked after exceeding max attempts ({data?.settings?.lockoutMinutes || 30} minutes).</li>
            <li><strong>Unlock:</strong> Removes the specific IP+path combination from the rate limit tracking.</li>
            <li><strong>Unlock IP:</strong> Removes ALL rate limit records for that IP address across all paths.</li>
            <li><strong>Clear All:</strong> Removes all rate limit records - use with caution!</li>
          </ul>
        </div>

        {/* Note about in-memory storage */}
        <div style={{
          background: 'rgba(251, 146, 60, 0.1)',
          border: '1px solid rgba(251, 146, 60, 0.3)',
          borderRadius: '12px',
          padding: 'var(--space-lg)',
          marginTop: 'var(--space-md)',
          color: '#fed7aa'
        }}>
          <strong>Note:</strong> Rate limit data is stored in-memory on each server. A server restart will clear all rate limits.
          If you have multiple backend servers, each server maintains its own rate limit records.
        </div>
      </div>
    </div>
  );
}

export default RateLimitManagement;
